/**
 * el-dialog 拖动 + 八方向拉伸指令
 *
 * 使用方式：
 * <el-dialog v-dialog-drag-resize title="标题" :visible.sync="dialogVisible">
 *   内容
 * </el-dialog>
 *
 * 实现原理：
 * - 拖动：通过 header 区域 mousedown → mousemove 实现
 * - 拉伸：在 dialog 内部边缘放置 8 个透明 handle，支持八方向调整大小
 * - 拉伸超过 body 默认 max-height 时，自动调整 body 高度自适应
 * - 使用 MutationObserver 监听 wrapper 的 display 变化来触发初始化
 * - 拖拽/拉伸后完整记录定位与尺寸，el-dialog 内部重渲染时自动恢复
 */

// 拉伸方向及其定位样式（handle 紧贴 dialog 内部边缘，不超出边界，避免被 overflow:hidden 裁剪）
const RESIZE_HANDLES = [
  { dir: 'n',  style: { top: '0', left: '0', right: '0', height: '6px', cursor: 'n-resize' } },
  { dir: 's',  style: { bottom: '0', left: '0', right: '0', height: '6px', cursor: 's-resize' } },
  { dir: 'e',  style: { right: '0', top: '0', bottom: '0', width: '6px', cursor: 'e-resize' } },
  { dir: 'w',  style: { left: '0', top: '0', bottom: '0', width: '6px', cursor: 'w-resize' } },
  { dir: 'ne', style: { top: '0', right: '0', width: '10px', height: '10px', cursor: 'ne-resize' } },
  { dir: 'nw', style: { top: '0', left: '0', width: '10px', height: '10px', cursor: 'nw-resize' } },
  { dir: 'se', style: { bottom: '0', right: '0', width: '10px', height: '10px', cursor: 'se-resize' } },
  { dir: 'sw', style: { bottom: '0', left: '0', width: '10px', height: '10px', cursor: 'sw-resize' } }
]

const MIN_WIDTH = 300
const MIN_HEIGHT = 200

/**
 * 初始化拖动与拉伸
 */
function initDragResize(wrapperEl) {
  const dialog = wrapperEl.querySelector('.el-dialog')
  const header = wrapperEl.querySelector('.el-dialog__header')
  if (!dialog || !header) return

  // 防止重复初始化
  if (dialog._dragResizeInit) return
  dialog._dragResizeInit = true

  // 完整记录用户自定义的定位与尺寸，el-dialog 重新渲染时据此恢复
  dialog._customSize = null
  // 交互锁：拖拽/拉伸进行中禁止 observer 恢复样式，避免互相干扰
  dialog._isInteracting = false

  // 监听 dialog 的 style 变化，el-dialog 内部重渲染覆盖样式时自动恢复用户状态
  const styleObserver = new MutationObserver(() => {
    if (dialog._isInteracting) return
    const cs = dialog._customSize
    if (!cs) return
    if (dialog.style.position !== cs.position) dialog.style.position = cs.position
    if (dialog.style.margin !== cs.margin) dialog.style.margin = cs.margin
    if (dialog.style.left !== cs.left) dialog.style.left = cs.left
    if (dialog.style.top !== cs.top) dialog.style.top = cs.top
    if (dialog.style.width !== cs.width) dialog.style.width = cs.width
    if (dialog.style.height !== cs.height) dialog.style.height = cs.height
  })
  styleObserver.observe(dialog, { attributes: true, attributeFilter: ['style'] })
  dialog._styleObserver = styleObserver

  // 将当前定位与尺寸快照存入 _customSize
  function saveCustomSize() {
    dialog._customSize = {
      position: dialog.style.position,
      margin: dialog.style.margin,
      left: dialog.style.left,
      top: dialog.style.top,
      width: dialog.style.width,
      height: dialog.style.height
    }
  }

  // dialog 使用 flex 纵向布局：body 弹性填充，footer 始终吸底
  dialog.style.display = 'flex'
  dialog.style.flexDirection = 'column'
  const body = dialog.querySelector('.el-dialog__body')
  if (body) {
    body.style.flex = '1'
    body.style.overflowY = 'auto'
    body.style.minHeight = '0'
  }

  // ==================== 拖动 ====================
  header.style.cursor = 'move'

  header.addEventListener('mousedown', (e) => {
    // 排除关闭按钮等可点击元素
    if (e.target.closest('.el-dialog__headerbtn')) return

    dialog._isInteracting = true
    const rect = dialog.getBoundingClientRect()
    const startX = e.clientX
    const startY = e.clientY

    // 切换到 fixed 定位，保持当前位置不跳动
    dialog.style.position = 'fixed'
    dialog.style.margin = '0'
    dialog.style.left = rect.left + 'px'
    dialog.style.top = rect.top + 'px'

    function onMouseMove(e) {
      dialog.style.left = (rect.left + e.clientX - startX) + 'px'
      dialog.style.top = (rect.top + e.clientY - startY) + 'px'
    }

    function onMouseUp() {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      saveCustomSize()
      dialog._isInteracting = false
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    e.preventDefault()
  })

  // ==================== 拉伸 ====================
  RESIZE_HANDLES.forEach(({ dir, style }) => {
    const handle = document.createElement('div')
    handle.className = 'el-dialog__resize-handle'

    Object.assign(handle.style, {
      position: 'absolute',
      zIndex: '10',
      background: 'transparent',
      ...style
    })

    handle.addEventListener('mousedown', (e) => {
      e.stopPropagation()
      e.preventDefault()

      dialog._isInteracting = true
      const rect = dialog.getBoundingClientRect()
      const startX = e.clientX
      const startY = e.clientY

      // 切换到 fixed 定位
      dialog.style.position = 'fixed'
      dialog.style.margin = '0'
      dialog.style.left = rect.left + 'px'
      dialog.style.top = rect.top + 'px'

      function onResizeMove(e) {
        const dx = e.clientX - startX
        const dy = e.clientY - startY

        if (dir.includes('e')) {
          dialog.style.width = Math.max(MIN_WIDTH, rect.width + dx) + 'px'
        }
        if (dir.includes('w')) {
          const newW = Math.max(MIN_WIDTH, rect.width - dx)
          dialog.style.width = newW + 'px'
          dialog.style.left = (rect.left + rect.width - newW) + 'px'
        }
        if (dir.includes('s')) {
          dialog.style.height = Math.max(MIN_HEIGHT, rect.height + dy) + 'px'
        }
        if (dir.includes('n')) {
          const newH = Math.max(MIN_HEIGHT, rect.height - dy)
          dialog.style.height = newH + 'px'
          dialog.style.top = (rect.top + rect.height - newH) + 'px'
        }
        // 涉及高度变化时，同步调整 body 的 max-height
        if (dir.includes('s') || dir.includes('n')) {
          const b = dialog.querySelector('.el-dialog__body')
          if (b && dialog.style.height) {
            b.style.maxHeight = 'none'
          }
        }
      }

      function onResizeUp() {
        document.removeEventListener('mousemove', onResizeMove)
        document.removeEventListener('mouseup', onResizeUp)
        saveCustomSize()
        dialog._isInteracting = false
      }

      document.addEventListener('mousemove', onResizeMove)
      document.addEventListener('mouseup', onResizeUp)
    })

    dialog.appendChild(handle)
  })
}

/**
 * 监听 wrapper 的 style 变化（v-show 切换 display），dialog 显示时初始化
 */
function setupObserver(el) {
  if (el._dragResizeObserver) return

  const observer = new MutationObserver(() => {
    if (el.style.display !== 'none') {
      setTimeout(() => initDragResize(el), 0)
    }
  })

  observer.observe(el, { attributes: true, attributeFilter: ['style'] })
  el._dragResizeObserver = observer
}

export default {
  inserted(el) {
    setupObserver(el)
    // dialog 初始就可见时直接初始化
    if (el.style.display !== 'none') {
      setTimeout(() => initDragResize(el), 0)
    }
  },
  unbind(el) {
    if (el._dragResizeObserver) {
      el._dragResizeObserver.disconnect()
      el._dragResizeObserver = null
    }
    const dialog = el.querySelector('.el-dialog')
    if (dialog && dialog._styleObserver) {
      dialog._styleObserver.disconnect()
      dialog._styleObserver = null
    }
  }
}
